import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { deletePartner, getPartner, updatePartner, updatePartnerAccount } from "services/partners/rest";
import { PartnerDto } from "services/partners/restModels";
import { useNotificationSnackbar } from "shared/ui/NotificationSnackbar/useNotificationSnackbar";

import { PartnerFormValues } from "features/Partners/components/PartnerFormDialog";

import { PartnerDetailsTab, TABS } from "../constants";

export type PartnerDetailsDialog = "edit" | "account" | "delete";

export const usePartnerDetailsState = (partnerId: number) => {
    const notify = useNotificationSnackbar();
    const history = useHistory();
    const location = useLocation();

    const [partner, setPartner] = useState<PartnerDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dialog, setDialog] = useState<PartnerDetailsDialog | null>(null);

    // Вкладка в адресе: возврат с карточки заведения вернул бы иначе на «Данные».
    const rawTab = new URLSearchParams(location.search).get("tab");
    const tab = TABS.some(({ key }) => key === rawTab) ? (rawTab as PartnerDetailsTab) : "info";

    const loadPartner = useCallback(async () => {
        setLoading(true);

        const response = await getPartner(partnerId).catch(() => null);

        setPartner(response);
        setLoading(false);
    }, [partnerId]);

    useEffect(() => {
        loadPartner();
    }, [loadPartner]);

    const run = async (action: () => Promise<void>, successTitle: string, failureTitle: string) => {
        setSaving(true);

        try {
            await action();
            notify.showSuccess(successTitle);
            setDialog(null);
        } catch (caught) {
            notify.showError(failureTitle, caught);
        } finally {
            setSaving(false);
        }
    };

    const handleSaveForm = ({ name, commissionPercent }: PartnerFormValues) =>
        run(
            async () => setPartner(await updatePartner(partnerId, { name, commissionPercent })),
            "Партнёр обновлён",
            "Не удалось сохранить партнёра"
        );

    const handleSaveAccount = (accountId: number) =>
        run(
            async () => setPartner(await updatePartnerAccount(partnerId, { accountId })),
            "Аккаунт партнёра изменён",
            "Не удалось сменить аккаунт"
        );

    const handleConfirmDelete = async () => {
        setSaving(true);

        try {
            await deletePartner(partnerId);
            history.push("/partners/general");
        } catch (caught) {
            notify.showError("Не удалось удалить партнёра", caught);
            setSaving(false);
        }
    };

    return {
        partner,
        loading,
        saving,
        dialog,
        tab,
        notify,
        onChangeTab: (next: PartnerDetailsTab) =>
            history.replace({ search: next === "info" ? "" : `tab=${next}` }),
        onOpenDialog: setDialog,
        onCloseDialog: () => setDialog(null),
        onSaveForm: handleSaveForm,
        onSaveAccount: handleSaveAccount,
        onConfirmDelete: handleConfirmDelete,
        onRetry: loadPartner,
    };
};
